import { Box, Button, Divider, Grid2, StackProps, Typography } from "@mui/material"
import { useEffect, useMemo, useRef, useState } from "react"
import { useNavigate, useParams } from "react-router-dom"
import {
  GetLocationRegisterQuery,
  LocationRegisterType,
  useCloseLocationRegisterMutation,
  useGetLocationRegisterSuspenseQuery,
  useGetLocationSessionSummaryByIdQuery,
  useGetRegisterSummaryByIdQuery
} from "src/graphql/quark/quark.type"
import { CenteredBox, PageLoader, Stack } from "src/shared/components"
import BorderStack from "src/shared/components/border-stack"
import OutlinedInput from "src/shared/components/outlined-input"
import { setBreadcrumbItem } from "src/shared/store/slices/breadcrumbs-slice"
import { useTypedDispatch, useTypedSelector } from "src/shared/store/store"
import { getDisplayPrice } from "src/shared/utils/helpers-functions"
import Denomination from "../../components/Denomination"
import ROUTE_PATHS from "src/routes/route-path"
import ConfirmationDialog from "src/shared/container/confirmation-dialog"
import { toast } from "react-toastify"
import { capitalizeFirstLetter } from "src/shared/utils/data-helpers"
import moment from "moment"
import XOutReportReceipt from "src/modules/invoices/thermal/xout-report-receipt"
import { useReactToPrint } from "react-to-print"
import { mapRegisterSalesToReceiptData } from "../../register-helper"
import AccessDenied from "src/shared/container/access-denied"
import { usePermission } from "src/shared/hooks/usePermission"
import { useTranslation } from "react-i18next"

const CloseRegister = () => {
  const { registerId } = useParams()
  const { hasPermission } = usePermission()
  const auth = useTypedSelector((state) => state.authentication)
  const dispatch = useTypedDispatch()
  const navigate = useNavigate()
  const [closingAmount, setClosingAmount] = useState<number | null>()
  const [closeRegisterDialog, setCloseRegisterDialog] = useState<boolean>(false)
  const [closingNote, setClosingNote] = useState("")
  const xoutReportRef = useRef(null) // Ref for the receipt component

  // ---- Graphql Queries Mutations -------
  const { data: registerResp } = useGetLocationRegisterSuspenseQuery({
    variables: { getLocationRegisterId: registerId as string, locationId: auth.currentWhLoc?.id as string },
    skip: !registerId
  })
  const registerSessionId = registerResp?.getLocationRegister.session?.id
  const { t } = useTranslation("sales-orders")
  const { data: registerSummary } = useGetRegisterSummaryByIdQuery({
    variables: { sessionId: registerSessionId as string },
    skip: !registerSessionId,
    fetchPolicy: "network-only"
  })
  const { data: registerSales, loading: loadingSalesRegister } = useGetLocationSessionSummaryByIdQuery({
    variables: { sessionId: registerSessionId as string },
    skip: !registerSessionId
  }) // to prefetch sales data

  const [closeRegister, { loading: closingRegister }] = useCloseLocationRegisterMutation()
  const register = registerResp?.getLocationRegister

  useEffect(() => {
    if (register?.id) {
      dispatch(setBreadcrumbItem({ id: registerId as string, label: register.name }))
      //   ---- reset closing note on first render or when register changes
      setClosingNote("")
    }
  }, [register])

  const handleClosingRegister = async () => {
    const resp = await closeRegister({
      variables: {
        registerSessionId: register?.session?.id as string,
        closeLocationRegisterSessionDto: {
          closingAmount: closingAmount ?? 0,
          ...(closingNote && { note: closingNote })
        }
      }
    })
    if (resp.data?.closeLocationRegister?.id) {
      toast.success(`Register: ${register?.name} is Closed successfully.`)
      return true
    }
    return false
  }
  const diffAmount = (register?.session?.balance ?? 0) - (closingAmount ?? 0)

  const receiptData = useMemo(() => {
    return mapRegisterSalesToReceiptData(registerSales?.getLocationSessionSummaryById || [], {
      ...register,
      lastClosedAt: moment().toISOString()
    } as GetLocationRegisterQuery["getLocationRegister"])
  }, [registerSales?.getLocationSessionSummaryById, register])
  const handleConfirmCloseRegister = async () => {
    const isClosed = await handleClosingRegister()
    if (isClosed) handlePrint()
  }

  const handlePrint = useReactToPrint({
    contentRef: xoutReportRef,
    documentTitle: "X-Out Report",
    onAfterPrint: () => {
      navigate(ROUTE_PATHS.REGISTER.BASE)
    }
  })

  // Validate the register session state:
  // - If the register is undefined, render nothing.
  // - If the register session is not "OPENED", show a message that the register is already closed
  //   and provide a button
  if (register === undefined) return <></>
  if (register?.type === LocationRegisterType.SalesRegister && !hasPermission("sales-orders:pos:close-cash-register"))
    return <AccessDenied />
  if (register.session?.status !== "OPENED")
    return (
      <CenteredBox height={"100%"}>
        <Typography>Register is already closed</Typography>
        <Button variant="outlined" size="small" sx={{ mt: 1 }} onClick={() => navigate(ROUTE_PATHS.REGISTER.BASE)}>
          Go Back
        </Button>
      </CenteredBox>
    )

  return (
    <Stack p={2}>
      <PageLoader isLoading={closingRegister || loadingSalesRegister} />
      <Stack variant="rowCenterSpaceBetween">
        <Stack direction={"column"} alignItems={"baseline"}>
          <Typography variant="h3">Close Register</Typography>
          <Typography variant="body2">{register?.name}</Typography>
        </Stack>
        <Stack direction={"column"} alignItems={"flex-end"}>
          <Typography variant="body2" textAlign={"right"}>
            Open By: <strong>{`${register?.lastOpenedBy?.firstName} ${register?.lastOpenedBy?.lastName}`}</strong>
          </Typography>
          <Typography variant="body2" textAlign={"right"}>
            <strong>{`${register?.lastOpenedAt ? moment(register?.lastOpenedAt).format("lll") : ""}`}</strong>
          </Typography>
        </Stack>
      </Stack>

      <Grid2 container spacing={2} mt={2}>
        <Grid2 size={{ xs: 6 }}>
          <Denomination currency={register?.currency as string} />
        </Grid2>
        <Grid2 size={{ xs: 6 }}>
          <BorderStack spacing={1}>
            <AmountBoxDisplay
              title="Opening Amount"
              amount={register?.session?.openingAmount ?? 0}
              currency={register?.currency ?? ""}
            />

            <Divider />
            <Typography variant="body2" fontWeight={600}>
              Sales
            </Typography>
            {registerSales?.getLocationSessionSummaryById?.map((sale) => (
              <AmountBoxDisplay
                pl={2}
                title={capitalizeFirstLetter((sale?.paymentMethod ?? "")?.replace("_", ""))}
                amount={sale?.netSale ?? 0}
                currency={register?.currency ?? ""}
              />
            ))}
            <Divider />
            <Typography variant="body2" fontWeight={600}>
              Returned
            </Typography>
            {registerSales?.getLocationSessionSummaryById?.map((sale) => (
              <AmountBoxDisplay
                pl={2}
                title={capitalizeFirstLetter((sale?.paymentMethod ?? "")?.replace("_", ""))}
                amount={sale?.returned ?? 0}
                currency={register?.currency ?? ""}
              />
            ))}

            <Divider />
            <AmountBoxDisplay
              title={t("register.cashRefunds")}
              amount={-(registerSummary?.getRegisterSummaryById?.cashRefundTotal ?? 0)}
              currency={register?.currency ?? ""}
            />
            <AmountBoxDisplay
              title="Balance"
              amount={register?.session?.balance ?? 0}
              currency={register?.currency ?? ""}
            />
            <AmountBoxDisplay title="Closing Amount" amount={closingAmount ?? 0} currency={register?.currency ?? ""} />
            <AmountBoxDisplay title="Difference Amount" amount={diffAmount} currency={register?.currency ?? ""} />
          </BorderStack>
          <Stack spacing={2} my={2}>
            <OutlinedInput
              multiline
              rows={2}
              sx={{ p: 0 }}
              label="Closing note"
              placeholder="Enter closing note"
              type="text"
              onChange={(e) => setClosingNote(e.target.value)}
              value={closingNote}
            />
            <OutlinedInput
              value={closingAmount}
              onChange={(e) => setClosingAmount(e.target.value ? Number(e.target.value) : null)}
              label="Closing amount"
              placeholder="Enter closing amount"
              type="number"
              className="numberinput"
            />
          </Stack>
          <Stack pb={2} direction={"row"} justifyContent={"flex-end"} spacing={2}>
            <Button variant="contained" onClick={() => setCloseRegisterDialog(true)}>
              Close Register
            </Button>
          </Stack>
        </Grid2>
      </Grid2>

      <ConfirmationDialog
        open={closeRegisterDialog}
        subtitle={
          <Typography sx={{ mt: 0.5 }} variant="body2" textAlign={"center"}>
            just a reminder... if any discrepancies, don't forget to adjust actual acounted amount before closing the
            register
          </Typography>
        }
        onConfirm={handleConfirmCloseRegister}
        toggleDialog={() => {
          setCloseRegisterDialog(false)
        }}
      />
      <Box sx={{ display: "none" }}>
        <XOutReportReceipt receiptRef={xoutReportRef} reportData={receiptData} />
      </Box>
    </Stack>
  )
}

export default CloseRegister

const AmountBoxDisplay = ({
  title,
  amount,
  currency,
  ...rest
}: { title: string; amount: number; currency: string } & StackProps) => {
  return (
    <Stack
      direction={"row"}
      justifyContent={"space-between"}
      sx={{ width: "100%", ...rest?.sx }}
      variant="columnCenter"
      {...rest}
    >
      <Typography variant="body2">{title}</Typography>
      <Typography variant="body1" fontWeight={600}>
        {getDisplayPrice(amount, `${currency} `)}
      </Typography>
    </Stack>
  )
}
